import React, { createContext, useMemo } from 'react';
import { ContentfulHomepage, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { HomepageProps } from '../views/Homepage/Homepage';
import { UseContentProps } from '../types';
import { useContentfulEntries } from '../hooks/useContentfulEntries/useContentfulEntries';
import { HomepageTransformer } from '../Contentful/transformers/HomepageTransformer';
import { ToProductCarouselBaseOptions } from '../Contentful/transformers/CarouselTransformer';

export const HomepagePropsContext = createContext<UseContentProps<HomepageProps> | null>(null);

export const PRODUCT_TRACKING_DATA: ToProductCarouselBaseOptions['productTrackingData'] = {
  descriptor: { page: 'Homepage' },
  listPrefix: 'Homepage - ',
};

export const HomepagePropsProvider = ({
  children,
  disclaimers,
  useInitialData,
  salesLineHours,
  contactBlockComponent,
}: {
  children: React.ReactNode;
  disclaimers?: Array<string>;
  useInitialData: () => ContentfulHomepage;
  salesLineHours?: string;
  contactBlockComponent?: React.VFC<any>;
}) => {
  const initialData = useInitialData();
  const { entries, isLoading, error } = useContentfulEntries<ContentfulHomepage>({
    filter: {
      content_type: EntryType.homepage,
      'sys.id': initialData?.sys?.id ?? '',
    },
    autoRefresh: window.Jbhifi.isContentPreview,
  });

  const data = entries?.length ? entries[0] : null;

  const transformedHomepageProps = useMemo(() => {
    return HomepageTransformer.toHomepageProps(data || initialData, { productTrackingData: PRODUCT_TRACKING_DATA });
  }, [initialData, data]);

  return (
    <HomepagePropsContext.Provider
      value={{
        data: {
          ...transformedHomepageProps,
          disclaimers,
          salesLineHours,
          contactBlockComponent,
        },
        isLoading,
        error,
      }}
    >
      {children}
    </HomepagePropsContext.Provider>
  );
};
